import React, { useState, useRef } from 'react';
import {
  Container, Typography, Box, Button, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Divider, LinearProgress
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import ContextNavBar from '../components/ContextNavBar';
import ModuleHeader from '../components/ModuleHeader';
import api from '../axios';
import Footer from '../components/Footer';

export default function ImportadorMembros({ onLogout }) {
  const navigate = useNavigate();
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.match(/\.(xlsx|xls)$/i)) {
      setErro('Selecione um arquivo .xlsx ou .xls.');
      setFile(null);
      return;
    }
    setErro('');
    setResultado(null);
    setFile(f);
  };

  const handleImportar = async () => {
    if (!file) return;
    setLoading(true);
    setErro('');
    setResultado(null);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/secretaria/importar-excel', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResultado(res.data);
    } catch (e) {
      setErro(e?.response?.data?.detail || 'Erro ao importar a planilha.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setResultado(null);
    setErro('');
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <>
      <Header onLogout={onLogout} />
      <ContextNavBar context="secretaria" />
      <Container maxWidth="md" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1, md: 2 } }}>

        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/secretaria')} size="small">
            Voltar
          </Button>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontSize: { xs: '1.2rem', md: '1.5rem' }, fontWeight: 800 }}>Importar Membros via Excel</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Envie uma planilha .xlsx com os dados dos membros. Linhas duplicadas (mesmo nome) são ignoradas.
          </Typography>
        </Box>

        {/* Instruções */}
        <Box sx={{ p: 2.5, bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200', borderRadius: 2, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Formato esperado da planilha</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ overflowX: 'auto', border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Coluna</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Obrigatório?</TableCell>
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Exemplo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[
                  { col: 'NOME', req: 'Sim', ex: 'João da Silva' },
                  { col: 'ESTADO CIVIL', req: 'Não', ex: 'Casado' },
                  { col: 'DATA DE NASCIMENTO', req: 'Não', ex: '15/04/1985' },
                  { col: 'TELEFONE', req: 'Não', ex: '(11) 91234-5678' },
                ].map((r) => (
                  <TableRow key={r.col}>
                    <TableCell><code>{r.col}</code></TableCell>
                    <TableCell>{r.req === 'Sim' ? <Chip label="Sim" color="error" size="small" /> : <Chip label="Não" size="small" />}</TableCell>
                    <TableCell>{r.ex}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            A primeira linha da planilha deve ser o cabeçalho. Os nomes das colunas não diferenciam maiúsculas/minúsculas.
          </Typography>
        </Box>

        {/* Upload */}
        <Box
          sx={{
            border: '2px dashed', borderColor: file ? 'success.main' : 'grey.300',
            borderRadius: 2, p: 4, textAlign: 'center', cursor: 'pointer',
            bgcolor: file ? 'success.50' : 'grey.50',
            transition: 'all 0.2s',
            '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' },
          }}
          onClick={() => inputRef.current?.click()}
        >
          <input ref={inputRef} type="file" accept=".xlsx,.xls" hidden onChange={handleFileChange} />
          <UploadFileIcon sx={{ fontSize: 48, color: file ? 'success.main' : 'grey.400', mb: 1 }} />
          {file ? (
            <>
              <Typography variant="body1" sx={{ fontWeight: 700, color: 'success.dark' }}>{file.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {(file.size / 1024).toFixed(1)} KB — clique para trocar
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>Clique para selecionar o arquivo</Typography>
              <Typography variant="caption" color="text.secondary">Formatos aceitos: .xlsx, .xls</Typography>
            </>
          )}
        </Box>

        {erro && <Alert severity="error" sx={{ mt: 2 }}>{erro}</Alert>}

        {/* Botões */}
        <Box sx={{ display: 'flex', gap: 2, mt: 3, flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleImportar}
            disabled={!file || loading}
            startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <UploadFileIcon />}
            sx={{ minWidth: 180 }}
          >
            {loading ? 'Importando...' : 'Importar'}
          </Button>
          {(file || resultado) && (
            <Button variant="outlined" onClick={handleReset} disabled={loading}>
              Limpar
            </Button>
          )}
        </Box>

        {loading && <LinearProgress sx={{ mt: 2, borderRadius: 1 }} />}

        {/* Resultado */}
        {resultado && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>Resultado da importação</Typography>

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`${resultado.total_importados} importados`}
                color="success"
                sx={{ fontWeight: 700, fontSize: 14, px: 1 }}
              />
              <Chip
                icon={<WarningIcon />}
                label={`${resultado.total_duplicados} duplicados (ignorados)`}
                color="warning"
                sx={{ fontWeight: 700, fontSize: 14, px: 1 }}
              />
              {resultado.total_erros > 0 && (
                <Chip
                  icon={<ErrorIcon />}
                  label={`${resultado.total_erros} erros`}
                  color="error"
                  sx={{ fontWeight: 700, fontSize: 14, px: 1 }}
                />
              )}
            </Box>

            {resultado.importados.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Membros importados ({resultado.importados.length})
                </Typography>
                <TableContainer component={Paper} elevation={1} sx={{ overflowX: 'auto', mb: 3, maxHeight: 280 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Linha</TableCell>
                        <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>Nome</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resultado.importados.map((r) => (
                        <TableRow key={r.linha}>
                          <TableCell>{r.linha}</TableCell>
                          <TableCell>{r.nome}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {resultado.duplicados.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'warning.dark' }}>
                  Duplicados ignorados ({resultado.duplicados.length})
                </Typography>
                <TableContainer component={Paper} elevation={1} sx={{ mb: 3, maxHeight: 200, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Linha</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Nome</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resultado.duplicados.map((r) => (
                        <TableRow key={r.linha}>
                          <TableCell>{r.linha}</TableCell>
                          <TableCell>{r.nome}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {resultado.erros.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'error.dark' }}>
                  Linhas com erro ({resultado.erros.length})
                </Typography>
                <TableContainer component={Paper} elevation={1} sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Linha</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Motivo</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resultado.erros.map((r) => (
                        <TableRow key={r.linha}>
                          <TableCell>{r.linha}</TableCell>
                          <TableCell>{r.motivo}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            <Button variant="outlined" onClick={() => navigate('/secretaria')}>
              Voltar para Secretaria
            </Button>
          </Box>
        )}
      </Container>
      <Footer />
    </>
  );
}
